import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import TermsConsentField from './TermsConsentField';
import { errorMessage, setTermsGateHandler } from '../../services/apiClient';
import { acceptVendorTerms } from '../../services/claimService';

/**
 * The action-gate half of vendor consent (mounted once in App). When any API
 * call returns 428 TERMS_AGREEMENT_REQUIRED, the apiClient interceptor calls
 * the handler registered here; accepting resolves the promise and the blocked
 * request retries transparently. Vendors who already consented via the claim
 * page (or signup) never see this - both paths share one consent record.
 */
export default function TermsAgreementGate() {
  const [gate, setGate] = useState(null); // { reason } while the modal is open
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Array, not a single slot: concurrent 428s (e.g. a dashboard firing two
  // vendor calls on load) all queue on the one open modal - overwriting would
  // leave the earlier request's promise hanging forever.
  const pending = useRef([]);

  useEffect(() => {
    setTermsGateHandler((details) => new Promise((resolve, reject) => {
      pending.current.push({ resolve, reject });
      setAgreed(false);
      setAgreeError('');
      setSubmitting(false);
      setGate((g) => g || { reason: details?.reason || 'never_accepted' });
    }));
    return () => setTermsGateHandler(null);
  }, []);

  if (!gate) return null;

  const settle = (fn) => {
    const waiters = pending.current;
    pending.current = [];
    waiters.forEach(fn);
  };

  const dismiss = () => {
    settle((w) => w.reject(new Error('Terms not accepted')));
    setGate(null);
  };

  const accept = async () => {
    if (!agreed) {
      setAgreeError('You must agree to the terms to continue');
      return;
    }
    setSubmitting(true);
    try {
      await acceptVendorTerms();
      settle((w) => w.resolve());
      setGate(null);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not record your acceptance.'));
      setSubmitting(false);
    }
  };

  const updated = gate.reason === 'terms_updated';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/50 p-4" role="dialog" aria-modal="true" aria-label="Vendor terms agreement">
      <div className="w-full max-w-md rounded-card border border-line bg-white p-6 shadow-lift sm:p-8">
        <h2 className="font-display text-2xl font-extrabold tracking-tight">
          {updated ? 'Our terms have changed' : 'One quick thing first'}
        </h2>
        <p className="mt-2 text-sm text-inksoft">
          {updated
            ? 'Please review and accept the updated terms to continue.'
            : 'Before you can take this action, please review and accept the VGO vendor terms.'}
        </p>
        <div className="mt-5">
          <TermsConsentField
            checked={agreed}
            onChange={(e) => { setAgreed(e.target.checked); setAgreeError(''); }}
            error={agreeError} />
        </div>
        <div className="mt-6 flex gap-3">
          <Button variant="ghost" className="flex-1" onClick={dismiss} disabled={submitting}>
            Not now
          </Button>
          <Button className="flex-1" onClick={accept} disabled={submitting}>
            {submitting ? 'Saving…' : 'Agree & continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
